import { useEffect, useState } from "react";
import { CloseOutlined, LogoutOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuItems } from "../../../utils/constants";
import { PATHS } from "../../../utils/paths";
import { logout } from "../../../api/Api.service";
import MobileBrandLogo from "../../MobileBrandLogo";
import { ReactComponent as MainBurgerIcon } from "../../../assets/burger/main-burger-icon.svg";
import { ReactComponent as ConversationBurgerIcon } from "../../../assets/burger/conversation-burger-icon.svg";
import { ReactComponent as LeaderboardBurgerIcon } from "../../../assets/burger/leaderbor-burger-icon.svg";
import { ReactComponent as FlowBurgerIcon } from "../../../assets/burger/flow-burger-icon.svg";
import { ReactComponent as ProfileBurgerIcon } from "../../../assets/burger/profile-burger-icon.svg";
import { ReactComponent as SettingsBurgerIcon } from "../../../assets/burger/settings-burger-icon.svg";
import { MOBILE_MENU_TOGGLE_EVENT } from "./events";
import {
  MobileMenuBackdrop,
  MobileMenuCloseButton,
  MobileMenuIcon,
  MobileMenuItem,
  MobileMenuLabel,
  MobileMenuLink,
  MobileMenuList,
  MobileMenuLogoLink,
  MobileMenuLogoutButton,
  MobileMenuPanel,
  MobileMenuRoot,
  MobileMenuHeader,
} from "./styled";

// Matches MobileMenuRoot's display breakpoint so the auto-close on resize
// fires exactly when the menu stops being rendered.
const DESKTOP_BREAKPOINT = 768;

const burgerMenuIcons = {
  [PATHS.Learn]: MainBurgerIcon,
  [PATHS.Conversations]: ConversationBurgerIcon,
  [PATHS.Leaderboard]: LeaderboardBurgerIcon,
  [PATHS.Feed]: FlowBurgerIcon,
  [PATHS.Profile]: ProfileBurgerIcon,
};

const MobileMenu = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = MenuItems(t);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleToggleMenu = () => {
      setIsOpen((previous) => !previous);
    };

    window.addEventListener(MOBILE_MENU_TOGGLE_EVENT, handleToggleMenu);

    return () => {
      window.removeEventListener(MOBILE_MENU_TOGGLE_EVENT, handleToggleMenu);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > DESKTOP_BREAKPOINT) {
        setIsOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  return (
    <MobileMenuRoot>
      <MobileMenuBackdrop
        type="button"
        disabled={!isOpen}
        aria-label={t("common.closeNavigation", {
          defaultValue: "Close navigation menu",
        })}
        aria-hidden={!isOpen}
        $open={isOpen}
        onClick={handleClose}
      />

      <MobileMenuPanel
        id="mobile-navigation-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("common.mobileNavigation", {
          defaultValue: "Mobile navigation",
        })}
        aria-hidden={!isOpen}
        $open={isOpen}
      >
        <MobileMenuHeader>
          <MobileMenuLogoLink
            to="/"
            aria-label={t("common.home", { defaultValue: "ProgPath home" })}
            onClick={handleClose}
            tabIndex={isOpen ? 0 : -1}
          >
            <MobileBrandLogo />
          </MobileMenuLogoLink>

          <MobileMenuCloseButton
            type="button"
            aria-label={t("common.closeNavigation", {
              defaultValue: "Close navigation menu",
            })}
            onClick={handleClose}
            tabIndex={isOpen ? 0 : -1}
          >
            <CloseOutlined />
          </MobileMenuCloseButton>
        </MobileMenuHeader>

        <MobileMenuList>
          {menuItems.map((item) => (
            <MobileMenuLink
              key={item.id}
              to={item.path}
              end={item.path === "/"}
              onClick={handleClose}
              tabIndex={isOpen ? 0 : -1}
            >
              {({ isActive }) => {
                const Icon = burgerMenuIcons[item.path] || item.icon;

                return (
                  <MobileMenuItem $isActive={isActive}>
                    <MobileMenuIcon>
                      <Icon />
                    </MobileMenuIcon>
                    <MobileMenuLabel>{item.title}</MobileMenuLabel>
                  </MobileMenuItem>
                );
              }}
            </MobileMenuLink>
          ))}

          <MobileMenuLink
            to="/settings"
            onClick={handleClose}
            tabIndex={isOpen ? 0 : -1}
          >
            {({ isActive }) => {
              const Icon = SettingsBurgerIcon;

              return (
                <MobileMenuItem $isActive={isActive}>
                  <MobileMenuIcon>
                    <Icon />
                  </MobileMenuIcon>
                  <MobileMenuLabel>{t("SETTINGS")}</MobileMenuLabel>
                </MobileMenuItem>
              );
            }}
          </MobileMenuLink>
          <MobileMenuLogoutButton
            type="button"
            onClick={handleLogout}
            tabIndex={isOpen ? 0 : -1}
          >
            <MobileMenuIcon>
              <LogoutOutlined aria-hidden="true" />
            </MobileMenuIcon>
            <MobileMenuLabel>{t("LOG_OUT")}</MobileMenuLabel>
          </MobileMenuLogoutButton>
        </MobileMenuList>
      </MobileMenuPanel>
    </MobileMenuRoot>
  );
};

export default MobileMenu;
